import type {
  ZoningQALanguage,
  ZoningQACitation,
  ZoneQAFeedbackType,
  ZoneDocumentFilter,
  ZoneDocumentsGroupedResponse,
} from '@popety-io/types'

/**
 * Zone context needed for Q&A
 */
export interface ZoneQAContext {
  zoneId: string
  zoneName: string
  municipalityId: string
  municipalityName: string
  regionId: string
  regionName: string
}

/**
 * Document filter for Zone Q&A (re-exported for convenience)
 */
export type { ZoneDocumentFilter } from '@popety-io/types'

/**
 * A single message in the conversation
 */
export interface ZoneQAMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  citations?: ZoningQACitation[]
  timestamp: Date
  suggestDocs?: boolean
}

/**
 * State for the Zone Q&A hook
 */
export interface ZoneQAState {
  isOpen: boolean
  sessionId: string | null
  messages: ZoneQAMessage[]
  isStreaming: boolean
  error: string | null
}

/**
 * Service interface for Zone Q&A API calls
 */
export interface ZoneQAService {
  askZoneStream: (
    type: 'general',
    id: string,
    request: {
      sessionId?: string
      message: string
      language: ZoningQALanguage
      zoneContext: {
        zone_id: string
        zone_name: string
        municipality_id: string
        municipality_name: string
        region_id: string
        region_name: string
      }
      documentFilters?: ZoneDocumentFilter[]
    }
  ) => AsyncGenerator<{
    type: 'session' | 'content' | 'citation' | 'suggest_docs' | 'done' | 'error'
    sessionId?: string
    text?: string
    citation?: ZoningQACitation
    messageId?: string
    error?: string
  }>
  /**
   * Submit feedback for a message (optional - only needed if feedback is enabled)
   */
  submitFeedback?: (
    messageId: string,
    rating: number,
    feedbackType?: ZoneQAFeedbackType,
    comment?: string
  ) => Promise<{ success: boolean; feedback_id: string }>
}

/**
 * Mapping from document source to ES granularity
 */
export const DOCUMENT_SOURCE_TO_GRANULARITY: Record<string, string> = {
  lup: 'land_use_plan',
  lupa: 'land_use_plan_assignment',
  lupo: 'land_use_plan_overlay',
  lupc: 'land_use_plan_cantonal',
  region: 'region',
  country: 'country',
}

/**
 * Helper function to build document filters from zone documents response
 */
export const buildDocumentFilters = (
  documentsResponse: ZoneDocumentsGroupedResponse | null | undefined
): ZoneDocumentFilter[] => {
  if (!documentsResponse?.documents_by_source) {
    return []
  }

  const filters: ZoneDocumentFilter[] = []
  const sources = documentsResponse.documents_by_source

  for (const [source, docs] of Object.entries(sources)) {
    if (docs && docs.length > 0) {
      for (const doc of docs) {
        const granularity = DOCUMENT_SOURCE_TO_GRANULARITY[source]
        if (granularity && doc.id) {
          filters.push({
            granularity: granularity as ZoneDocumentFilter['granularity'],
            document_id: parseInt(doc.id, 10),
            source: source as ZoneDocumentFilter['source'],
          })
        }
      }
    }
  }

  return filters
}

/**
 * Translations for Zone Q&A
 */
export interface ZoneQATranslations {
  title?: string
  inLocation?: string
  placeholder?: string
  send?: string
  loading?: string
  emptyState?: string
  suggestDocs?: string
  errorRetry?: string
  citationLabels?: {
    communal?: string
    cantonal?: string
  }
  feedback?: {
    helpful?: string
    notHelpful?: string
    thankYou?: string
    feedbackError?: string
  }
}

/**
 * Props for ZoneQAPanel component
 */
export interface ZoneQAPanelProps {
  zoneContext: ZoneQAContext
  zoningService: ZoneQAService
  language: ZoningQALanguage
  translations?: ZoneQATranslations
  onViewDocs?: () => void
  /** Callback to transcribe voice audio. Receives an audio Blob, returns transcribed text. */
  onTranscribeVoice?: (audioBlob: Blob) => Promise<string>
}
